import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Save,
  RefreshCw,
  Mail,
  FileText,
  AlertCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';

const ExpiryReminderSettings = () => {
  usePageTitle('Expiry Reminder Settings');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    sender_email: '',
    sender_name: '',
    subject_template: '',
    subject_template_expired: '',
    body_template: '',
    body_template_expired: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/expiry-reminders/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/expiry-reminders/settings`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Access check - Admin only
  if (user?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
        <p className="text-slate-500 mt-2">Only administrators can modify these settings.</p>
        <Button onClick={() => navigate('/expiry-reminders')} className="mt-4">
          Return to Expiry Reminders
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-[#2C9DE1]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[900px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/expiry-reminders')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Expiry Reminder Settings
            </h1>
            <p className="text-slate-500 text-sm">Configure the email template for certificate expiry reminders</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#2C9DE1] hover:bg-[#1A7BB8]">
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Sender Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Mail className="w-5 h-5 text-[#2C9DE1]" />
              Sender Settings
            </CardTitle>
            <CardDescription>
              Configure who the expiry reminder emails are sent from
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sender_name">Sender Name</Label>
                <Input
                  id="sender_name"
                  value={settings.sender_name}
                  onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })}
                  placeholder="EMP Medic Training"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sender_email">Sender Email</Label>
                <Input
                  id="sender_email"
                  type="email"
                  value={settings.sender_email}
                  onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                  placeholder="info@empmedic.com"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Templates with Tabs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <FileText className="w-5 h-5 text-[#2C9DE1]" />
              Email Templates
            </CardTitle>
            <CardDescription>
              Customize the email content for expiring and expired certificates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="expiring" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="expiring" className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Expiring Soon
                </TabsTrigger>
                <TabsTrigger value="expired" className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Already Expired
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="expiring" className="space-y-4">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  This template is used when notifying about certificates that will expire in 30, 60, or 90 days.
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject_template">Email Subject</Label>
                  <Input
                    id="subject_template"
                    value={settings.subject_template}
                    onChange={(e) => setSettings({ ...settings, subject_template: e.target.value })}
                    placeholder="Certificate Expiry Reminder - {{company_name}}"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="body_template">Email Body</Label>
                  <Textarea
                    id="body_template"
                    value={settings.body_template}
                    onChange={(e) => setSettings({ ...settings, body_template: e.target.value })}
                    rows={12}
                    className="font-mono text-sm"
                    placeholder="Enter email template for expiring certificates..."
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="expired" className="space-y-4">
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  This template is used when notifying about certificates that have already expired.
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject_template_expired">Email Subject</Label>
                  <Input
                    id="subject_template_expired"
                    value={settings.subject_template_expired}
                    onChange={(e) => setSettings({ ...settings, subject_template_expired: e.target.value })}
                    placeholder="Certificate Expiry Notice - {{company_name}}"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="body_template_expired">Email Body</Label>
                  <Textarea
                    id="body_template_expired"
                    value={settings.body_template_expired}
                    onChange={(e) => setSettings({ ...settings, body_template_expired: e.target.value })}
                    rows={12}
                    className="font-mono text-sm"
                    placeholder="Enter email template for expired certificates..."
                  />
                </div>
              </TabsContent>
            </Tabs>
            
            <p className="text-xs text-slate-500 mt-4">
              Available placeholders: {"{{company_name}}"}, {"{{primary_contact}}"}, {"{{student_list}}"}, {"{{expiry_window}}"}
            </p>
          </CardContent>
        </Card>

        {/* Placeholder Reference */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Template Placeholders
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <code className="bg-blue-100 px-1 rounded text-blue-800">{"{{company_name}}"}</code>
                <p className="text-blue-700 mt-1">The company's name</p>
              </div>
              <div>
                <code className="bg-blue-100 px-1 rounded text-blue-800">{"{{primary_contact}}"}</code>
                <p className="text-blue-700 mt-1">The primary contact's name</p>
              </div>
              <div>
                <code className="bg-blue-100 px-1 rounded text-blue-800">{"{{student_list}}"}</code>
                <p className="text-blue-700 mt-1">Auto-generated list of students and their certificates</p>
              </div>
              <div>
                <code className="bg-blue-100 px-1 rounded text-blue-800">{"{{expiry_window}}"}</code>
                <p className="text-blue-700 mt-1">Number of days (30, 60, 90) or -1 for expired</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ExpiryReminderSettings;
